import React, { lazy } from 'react'
import { useParams, useQueryParams } from '@fs/zion-router'

import * as permissions from 'config/permissions'
import { useFeatureFlag } from '@fs/zion-flags'

const SearchResultsPage = lazy(() => import('views/SearchResultsPage/SearchResultsPage'))
const VolumeSetSearchResultsPage = lazy(
  () => import('views/SearchResultsPage/VolumeSetSearch/VolumeSetSearchResultsPage/VolumeSetSearchResultsPage')
)
const COMHome = lazy(() => import('views/CameraOperator/Manager/Home/COMHome'))

export default function useRoutes() {
  const newCameraOperatorPage = useFeatureFlag('lightyear_lightyearReact_newCameraOperatorManagementPage')?.isOn
  const CameraOperatorManagement = React.useMemo(
    () =>
      lazy(() =>
        newCameraOperatorPage
          ? import('views/CameraOperator/Manager/NewDashboard/Dashboard')
          : import('views/CameraOperator/Manager/Dashboard/COMDashboard')
      ),
    [newCameraOperatorPage]
  )
  const CameraOperatorDetails = React.useMemo(
    () =>
      lazy(() =>
        newCameraOperatorPage
          ? import('views/CameraOperator/Details/Details')
          : import('views/CameraOperator/Manager/Dashboard/Operator/Operator')
      ),
    [newCameraOperatorPage]
  )
  const routes = {
    // out of alphabetical order to prioritize the /audit/feedback route over the /audit/:id route
    cameraOperatorFeedbackDashboard: {
      auth: true,
      Component: lazy(() => import('views/CameraOperator/Feedback/Dashboard/COFDashboard')),
      developmentOnly: false,
      path: ['/audit/feedback/:projectId', '/home/audit/feedback/:projectId'],
      permissions: permissions.cameraOperatorFeedback,
    },
    cameraOperatorFeedbackProjects: {
      auth: true,
      Component: lazy(() => import('views/CameraOperator/Feedback/Dashboard/COFProjects')),
      developmentOnly: false,
      path: ['/audit/feedback', '/home/audit/feedback'],
      permissions: permissions.cameraOperatorFeedback,
    },
    cameraOperatorFeedbackOperator: {
      auth: true,
      Component: lazy(() => import('views/CameraOperator/Feedback/OperatorView/COFOperatorView')),
      developmentOnly: false,
      path: ['/audit/feedback/:projectId/project', '/home/audit/feedback/:projectId/project'],
      permissions: permissions.cameraOperatorFeedback,
    },
    // Remove once shared-image-viewer returns auditors to /records/home/audit instead of /records/audit (OPSYS-143).
    auditRedirect: {
      auth: false,
      Component: lazy(() => import('views/AuditRedirect/AuditRedirect')),
      developmentOnly: false,
      path: ['/audit', '/audit/:id'],
      permissions: permissions.authorized,
    },
    collectionsSearch: {
      auth: true,
      Component: lazy(() => import('views/CollectionsSearch/SearchSteps/CollectionsSearch')),
      developmentOnly: false,
      path: ['/collections-search', '/collections/search', '/images/collections-search', '/images/collections/search'],
      permissions: permissions.authorized,
    },
    collectionSearchResults: {
      auth: true,
      Component: lazy(() => import('views/CollectionsSearch/CollectionsResults/CollectionsResults')),
      developmentOnly: false,
      path: [
        '/collections-results',
        '/collections/results',
        '/images/collections-results',
        '/images/collections/results',
      ],
      permissions: permissions.authorized,
    },
    historyPage: {
      auth: true,
      Component: lazy(() => import('views/HistoryPage/HistoryPage')),
      developmentOnly: false,
      path: '/images/history',
      permissions: permissions.internalSearch,
    },
    historyPageSearchable: {
      auth: true,
      Component: lazy(() => import('views/HistoryPage/HistoryPage')),
      developmentOnly: false,
      path: '/images/history/:id',
      permissions: permissions.internalSearch,
    },
    imageOrganizeProjectsDashboard: {
      auth: true,
      Component: lazy(() => import('views/ImageOrganizeProjectsDashboard/ImageOrganizeProjectsDashboard')),
      developmentOnly: false,
      path: '/images/projects-dashboard',
      permissions: permissions.lightyearAdmin,
    },
    imageViewer: {
      auth: false,
      Component: () => {
        const { groupId } = useParams() // eslint-disable-line react-hooks/rules-of-hooks
        window.location.replace(`http://localhost:5001/ark:/61903/${groupId}?view=explore`)
        return null
      },
      developmentOnly: true,
      path: '/records/r2/ark:/61903/:groupId',
      permissions: permissions.authorized,
    },
    landingPage: {
      auth: true,
      Component: lazy(() => import('views/LandingPage/LandingPage')),
      developmentOnly: false,
      path: ['/images', '/beta'],
      permissions: permissions.authorized,
    },
    cameraOperatorManagerHome: {
      auth: true,
      Component: COMHome,
      developmentOnly: false,
      path: ['/operator/manager', '/home/operator/manager'],
      permissions: permissions.cameraOperatorManager,
    },
    cameraOperatorFeedbackDashboardWeekPage: {
      auth: true,
      Component: lazy(
        () => import('views/CameraOperator/Projects/Dashboard/ProjectDashboardWeekPage/ProjectDashboardWeekPage')
      ),
      path: ['/operator/:weekEnding', '/home/operator/:weekEnding'],
      permissions: permissions.cameraOperator,
    },
    cameraOperatorDashboard: {
      auth: true,
      Component: lazy(() => import('views/CameraOperator/Projects/Dashboard/COPDashboard')),
      path: ['/operator', '/home/operator'],
      permissions: permissions.cameraOperator,
    },
    cameraOperator: {
      auth: true,
      Component: CameraOperatorDetails,
      developmentOnly: false,
      path: ['/operators/:id', '/home/operators/:id'],
      permissions: permissions.cameraOperatorManagement,
    },
    cameraOperatorManagementDashboard: {
      auth: true,
      Component: CameraOperatorManagement,
      developmentOnly: false,
      path: ['/operators', '/home/operators'],
      permissions: permissions.cameraOperatorManagement,
    },
    projectSupervisorDashboard: {
      auth: true,
      Component: lazy(() => import('views/ProjectSupervisorDashboard/ProjectSupervisorDashboard')),
      developmentOnly: false,
      path: ['/projects/supervisor', '/home/projects/supervisor'],
      permissions: permissions.contractorSupervisor,
    },
    recordsHomePage: {
      auth: true,
      Component: lazy(() => import('views/RecordsHomePage/RecordsHomePage')),
      developmentOnly: false,
      path: ['/home'],
      permissions: permissions.authorized,
    },
    searchResultsPage: {
      auth: true,
      Component: () => {
        const { query } = useQueryParams() // eslint-disable-line react-hooks/rules-of-hooks
        return query.volumeSetSearch ? <VolumeSetSearchResultsPage /> : <SearchResultsPage />
      },
      developmentOnly: false,
      path: ['/images/search-results', '/beta/search-results'],
      permissions: permissions.authorized,
    },
    testPage: {
      auth: false,
      Component: lazy(() => import('views/TestPage/TestPage')),
      developmentOnly: true,
      path: '/test',
      permissions: permissions.authorized,
    },
    volumeSetImageOrganizeOrganize: {
      auth: true,
      Component: lazy(() => import('views/VolumeSetImageOrganize/organize/OrganizePage')),
      developmentOnly: false,
      path: '/images/organize/:volumeSetType(jiapu|jokbo|keizu)/:groupId',
      permissions: permissions.volumeSetImageOrganize,
    },
    volumeSetImageOrganizeSearch: {
      auth: true,
      Component: lazy(() => import('views/VolumeSetImageOrganize/search/SearchPage')),
      developmentOnly: false,
      path: '/images/organize/:volumeSetType(jiapu|jokbo|keizu)',
      permissions: permissions.volumeSetImageOrganize,
    },
    imageViewerRedirect: {
      auth: true,
      Component: lazy(() => import('views/ImageViewerRedirect/ImageViewerRedirect')),
      developmentOnly: false,
      path: '/images/redirect/:groupId',
      permissions: permissions.authorized,
    },
    contractInvoiceReview: {
      auth: true,
      Component: lazy(() => import('views/ContractInvoiceReview/ContractInvoiceReview')),
      developmentOnly: false,
      path: '/home/finance/review',
      permissions: permissions.contractInvoiceReview,
    },
    invoiceHistory: {
      auth: true,
      Component: lazy(() => import('views/ContractorInvoiceHistory/InvoiceHistory')),
      developmentOnly: false,
      path: '/projects/supervisor/invoice-history',
      permissions: permissions.contractorSupervisor,
    },
    watermarking: {
      auth: true,
      Component: lazy(() => import('views/Watermarking/WatermarkingView')),
      developmentOnly: false,
      path: '/home/watermarking',
      permissions: permissions.watermarking,
    },
    watermarkingDetails: {
      auth: true,
      Component: lazy(() => import('views/Watermarking/NewWatermarking/WatermarkDetails/WatermarkDetails')),
      developmentOnly: false,
      path: '/home/watermarking/:watermark_id',
      permissions: permissions.watermarking,
    },
  }

  return {
    chinaOnlyRoutes: [
      routes.imageViewer,
      { ...routes.landingPage, auth: false },
      { ...routes.searchResultsPage, auth: false },
      routes.testPage,
    ],
    routes: Object.values(routes),
  }
}
